import { useQuery } from "@tanstack/react-query";
// Use the data provider's validated resolver (origin-allowlisted per #5097) — do
// not re-implement partner URL resolution, or this token-bearing call becomes an
// unvalidated exfiltration sink.
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type { PolicyDashboardAnalytics } from "@/types";

interface DashboardFilters {
  vendor_id_ref?: string | string[];
  client_id_ref?: string | string[];
  tpa_vendor_id?: string | string[];
  rx_vendor_id?: string | string[];
}

/**
 * Fetches all dashboard data from the single analytics endpoint.
 * Replaces 4+ separate API calls + endorsement batching with one request.
 */
export const usePolicyDashboardData = (filters: DashboardFilters) => {
  return useQuery<PolicyDashboardAnalytics>({
    queryKey: ["policy-dashboard-analytics", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      const appendFilter = (key: string, value?: string | string[]) => {
        if (!value) return;
        const values = Array.isArray(value) ? value : [value];
        values.forEach((v) => params.append(key, v));
      };
      appendFilter("vendor_id_ref", filters.vendor_id_ref);
      appendFilter("client_id_ref", filters.client_id_ref);
      appendFilter("tpa_vendor_id", filters.tpa_vendor_id);
      appendFilter("rx_vendor_id", filters.rx_vendor_id);

      const qs = params.toString();
      const url = `${getPartnerAwareApiUrl()}/policies/dashboard/analytics${qs ? `?${qs}` : ""}`;
      const { json } = await httpClient(url);
      return json as PolicyDashboardAnalytics;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
